import React, { useEffect, useMemo, useState } from 'react';
import { Factory, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { machinesAPI, workOrdersAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const AUTO = '__auto__';

export function CreateWorkOrderFromOrderDialog({ open, onOpenChange, order, workOrders = [], onCreated }) {
  const [machines, setMachines] = useState([]);
  const [itemId, setItemId] = useState('');
  const [qty, setQty] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [machineId, setMachineId] = useState(AUTO);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);

  const lines = useMemo(() => (order?.items || []).map((item) => {
    const planned = workOrders
      .filter((wo) => wo.sales_order_item_id === item.id && wo.status !== 'cancelled')
      .reduce((sum, wo) => sum + (wo.qty_target || 0), 0);
    const ordered = item.quantity || 0;
    return { ...item, planned, ordered, remaining: Math.max(ordered - planned, 0) };
  }), [order, workOrders]);

  const selected = lines.find((line) => line.id === itemId);

  useEffect(() => {
    if (!open) return;
    machinesAPI.list({ limit: 200 })
      .then((res) => setMachines(res.data?.items || res.data || []))
      .catch(() => setMachines([]));
    const firstOpen = lines.find((line) => line.remaining > 0) || lines[0];
    setItemId(firstOpen?.id || '');
    setQty(firstOpen ? String(firstOpen.remaining || firstOpen.ordered || '') : '');
    setStartDate(''); setEndDate(''); setMachineId(AUTO); setNotes('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickItem = (value) => {
    setItemId(value);
    const line = lines.find((row) => row.id === value);
    setQty(line ? String(line.remaining || '') : '');
  };

  const submit = async () => {
    const quantity = parseFloat(qty);
    if (!itemId) { toast.error('Choose the order line to produce'); return; }
    if (!quantity || quantity <= 0) { toast.error('Enter the quantity to produce'); return; }
    if (selected && quantity > selected.remaining) {
      toast.error(`Only ${selected.remaining} pcs are still unplanned on this line`);
      return;
    }
    setCreating(true);
    try {
      const response = await workOrdersAPI.create({
        sales_order_id: order.id,
        sales_order_item_id: itemId,
        qty_target: quantity,
        assigned_machine_id: machineId === AUTO ? null : machineId,
        planned_start_date: startDate || null,
        planned_end_date: endDate || null,
        priority: 2,
        production_notes: notes || null,
      });
      const created = response.data?.work_orders || (response.data?.wo_number ? [response.data] : []);
      toast.success(created.length > 1
        ? `${created.length} Work Orders created from the kit BOM`
        : `Work Order ${created[0]?.wo_number || ''} created`);
      onOpenChange(false);
      onCreated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create the Work Order');
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="create-wo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Factory className="h-5 w-5 text-blue-600" /> Create Work Order
          </DialogTitle>
          <DialogDescription>
            For {order?.order_number}. Kit lines automatically explode into one Work Order per BOM component,
            each routed to its own machine.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Order line</Label>
            <Select value={itemId} onValueChange={pickItem}>
              <SelectTrigger data-testid="create-wo-item-select"><SelectValue placeholder="Choose a line item" /></SelectTrigger>
              <SelectContent>
                {lines.map((line, index) => (
                  <SelectItem key={line.id} value={line.id} disabled={line.remaining <= 0}>
                    {index + 1}. {line.part_internal_no || line.description || 'Item'} · {line.remaining} of {line.ordered} pcs left
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && (
              <p className="text-xs text-muted-foreground" data-testid="create-wo-line-summary">
                {selected.description || '—'} · ordered {selected.ordered} · already planned {selected.planned} ·
                {' '}unplanned {selected.remaining}
              </p>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Quantity to produce</Label>
              <Input type="number" min="1" value={qty} onChange={(event) => setQty(event.target.value)}
                data-testid="create-wo-qty-input" />
            </div>
            <div className="space-y-2">
              <Label>Machine (optional)</Label>
              <Select value={machineId} onValueChange={setMachineId}>
                <SelectTrigger data-testid="create-wo-machine-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUTO}>Auto / assign later</SelectItem>
                  {machines.map((machine) => (
                    <SelectItem key={machine.id} value={machine.id}>
                      {machine.machine_code || machine.name || machine.machine_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Planned start (optional)</Label>
              <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)}
                data-testid="create-wo-start-input" />
            </div>
            <div className="space-y-2">
              <Label>Planned end (optional)</Label>
              <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)}
                data-testid="create-wo-end-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Production notes (optional)</Label>
            <Input value={notes} onChange={(event) => setNotes(event.target.value)}
              placeholder="Anything the shop floor should know" data-testid="create-wo-notes-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="create-wo-cancel">Cancel</Button>
          <Button onClick={submit} disabled={creating} data-testid="create-wo-submit">
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Factory className="mr-2 h-4 w-4" />}
            Create Work Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateWorkOrderFromOrderDialog;
